import React from 'react'
import { Globe, BookOpen, Eye, Users, FileText, Sword, Zap, Map, Crown, MessageSquare } from 'lucide-react'

const Features: React.FC = () => {
  const coreValues = [
    {
      icon: Globe,
      title: 'Virtual Third Space',
      description: 'A welcoming digital environment where everyone can find community, belonging, and shared adventure beyond home and work.',
      color: 'blue',
    },
    {
      icon: BookOpen,
      title: 'Text Adventure Core',
      description: 'Text adventures at the heart of gameplay, enhanced by modern conveniences while preserving the magic of imagination.',
      color: 'green',
    },
    {
      icon: Eye,
      title: 'Immersive Experience',
      description: 'Rich storytelling and deep environments that reward exploration and replayability over massive scope.',
      color: 'purple',
    },
    {
      icon: Users,
      title: 'Accessibility First',
      description: 'Designed for everyone - new players discovering MUDs and veterans who appreciate text-based gaming accessibility.',
      color: 'orange',
    },
    {
      icon: FileText,
      title: 'Transparent Development',
      description: 'Clear roadmaps, open goals, active community feedback, and honest communication about progress and challenges.',
      color: 'cyan',
    },
  ]

  const gameFeatures = [
    {
      icon: Sword,
      title: 'Strategic Combat',
      description: 'Turn-based combat system emphasizing tactical decision-making over button-mashing, with meaningful character progression.',
      status: 'In Development',
    },
    {
      icon: Crown,
      title: 'Character Classes',
      description: 'Distinct classes with unique abilities and playstyles - Warrior, Mage, and Rogue each offering different approaches to adventure.',
      status: 'Core Complete',
    },
    {
      icon: Map,
      title: 'Rich World Building',
      description: 'Carefully crafted areas with detailed descriptions, environmental storytelling, and secrets that reward thorough exploration.',
      status: 'Expanding',
    },
    {
      icon: Zap,
      title: 'Real-Time Features',
      description: 'Live chat, real-time interactions, and dynamic world events that make the game feel alive and responsive.',
      status: 'Active',
    },
    {
      icon: MessageSquare,
      title: 'Quest System',
      description: 'Engaging storylines with meaningful choices, consequences that matter, and multiple paths through adventures.',
      status: 'In Development',
    },
  ]

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-600 text-blue-400 border-blue-500/30',
      green: 'bg-green-600 text-green-400 border-green-500/30',
      purple: 'bg-purple-600 text-purple-400 border-purple-500/30',
      orange: 'bg-orange-600 text-orange-400 border-orange-500/30',
      cyan: 'bg-cyan-600 text-cyan-400 border-cyan-500/30',
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Core Values Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What Makes HammerMUD Special
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Our core values guide every decision in building a text-based gaming experience 
            that honors MUD traditions while embracing modern possibilities
          </p>
        </div>

        {/* Core Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {coreValues.map((value, index) => {
            const colorClasses = getColorClasses(value.color)
            const [bgColor, textColor, borderColor] = colorClasses.split(' ')
            
            return (
              <div
                key={value.title}
                className={`bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 animate-fade-in border ${borderColor}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 ${bgColor} rounded-lg flex items-center justify-center mr-4`}>
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className={`text-lg font-semibold ${textColor}`}>
                    {value.title}
                  </h3>
                </div>
                <p className="text-gray-300 leading-relaxed text-sm">
                  {value.description}
                </p>
              </div>
            )
          })}
        </div>

        {/* Game Features Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Game Features in Development
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Building a modern MUD with classic depth - here's what we're working on to bring 
            the best of text-based gaming to life
          </p>
        </div>

        {/* Game Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {gameFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className="bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 animate-fade-in border border-gray-700"
              style={{ animationDelay: `${(index + 5) * 0.1}s` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mr-4">
                    <feature.icon className="w-6 h-6 text-gray-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {feature.title}
                    </h3>
                  </div>
                </div>
                <span className="bg-blue-900/50 text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
                  {feature.status}
                </span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-lg p-8">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Join Our Community?</h3>
          <p className="text-lg text-gray-300 mb-6">
            Follow our development journey and be part of the conversation shaping HammerMUD's future
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/blog"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Read Development Blog
            </a>
            <a
              href="/features"
              className="inline-flex items-center px-6 py-3 border-2 border-blue-400 text-blue-400 rounded-lg hover:bg-blue-400 hover:text-white transition-colors duration-200 font-medium"
            >
              Explore All Features
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Features